import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookIcon,
  UsersIcon,
  ActivityIcon,
  MessageCircleIcon,
  VideoIcon
} from '../Icons';
import { ChevronDownIcon } from 'lucide-react';

type SubItem = {
  path: string;
  label: string;
  icon?: string;
  unreadCount?: number;
};

type SidebarSectionProps = {
  id: string;
  label: string;
  icon: string;
  isExpanded: boolean;
  showNewUserIndicator?: boolean;
  disabled?: boolean;
  onToggle: (id: string) => void;
  onLinkClick: () => void;
  subItems: SubItem[];
  isActivePath: (path: string) => boolean;
  onReadCategory?: (path: string) => void;
};

const SidebarSection: React.FC<SidebarSectionProps> = ({
  id,
  label,
  icon,
  isExpanded,
  showNewUserIndicator = false,
  onToggle,
  onLinkClick,
  subItems,
  isActivePath,
  disabled,
  onReadCategory
}) => {
  const renderIcon = () => {
    const baseClass = 'text-gray-700';
    const activeClass = id === 'comece' && showNewUserIndicator ? 'text-terracota' : baseClass;

    switch (icon) {
      case 'book': return <BookIcon className={baseClass} />;
      case 'video': return <VideoIcon className={baseClass} />;
      case 'users': return <UsersIcon className={baseClass} />;
      case 'message-circle': return <MessageCircleIcon className={baseClass} />;
      case 'activity': return <ActivityIcon className={activeClass} />;
      default: return null;
    }
  };

  return (
    <li key={id}>
      <button
        disabled={disabled}
        onClick={() => onToggle(id)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
          id === 'comece' && showNewUserIndicator
            ? 'bg-gradient-to-r from-terracota/10 to-marrom-escuro/10 border-2 border-terracota/30 text-terracota'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <div>
          {renderIcon()}
        </div>
        <span className={`text-sm font-medium ${id === 'comece' && showNewUserIndicator ? 'text-terracota' : ''}`}>
          {label}
        </span>
        <ChevronDownIcon
          size={16}
          className={`ml-auto text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />

        {/* Indicador pulsante para novas alunas */}
        {id === 'comece' && showNewUserIndicator && (
          <div className="absolute -right-1 -top-1">
            <div className="relative">
              <div className="w-3 h-3 bg-terracota rounded-full animate-pulse"></div>
              <div className="absolute inset-0 w-3 h-3 bg-terracota rounded-full animate-ping opacity-75"></div>
            </div>
          </div>
        )}
      </button>

      {isExpanded && (
        <ul className="ml-8 mt-2 space-y-1">
          {subItems.map(subItem => {
            const subActive = isActivePath(subItem.path);
            return (
              <li key={subItem.path}>
                <Link
                  to={subItem.path}
                  onClick={() => {
                    onLinkClick();
                    if (id === 'comece' && showNewUserIndicator) {
                      localStorage.setItem('visited_comece_section', 'true');
                    }
                    if (onReadCategory) {
                      onReadCategory(subItem.path);
                    }
                  }}
                  className={`flex items-center justify-between px-4 py-2 rounded-lg transition-colors ${
                    subActive ? 'bg-terracota/20 text-terracota' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {subItem.icon && <span className="text-sm">{subItem.icon}</span>}
                    <span className="text-sm">{subItem.label}</span>
                        {subItem?.unreadCount && subItem.unreadCount > 0 
                        ? (subItem.unreadCount > 99 ? '99+' : 
                          <span className="bg-terracota text-white text-[8px] px-2 py-1 rounded-full min-w-[16px] text-center">{subItem.unreadCount}</span>) 
                        : null}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export default SidebarSection;
